import { Box, Stack } from "@mui/material";
import { useCancelTransactionMutation } from "Api/Mutations/Transactions/useCancelTransactionMutation";
import { TransactionActionButtons } from "Components/Transactions/TransactionActionButtons/TransactionActionButtons";
import { CancelTransactionButton } from "Components/Transactions/TransactionDetailActions/CancelTransactionButton";
import { PayTransactionButton } from "Components/Transactions/TransactionDetailActions/PayTransactionButton";
import { RevertTransactionButton } from "Components/Transactions/TransactionDetailActions/RevertTransactionButton";
import { useIsCurrentUserEmployee } from "Hooks/useIsCurrentUserEmployee";
import {
  type TransactionDetailDto,
  TransactionDirection,
  TransactionStatus,
} from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import type { ViewerTransactionDirection } from "Utils/TransactionUtils";
import { appUrl } from "Utils/UrlUtils";

type Props = {
  publicID: string;
  isLoadingStatus: boolean;
  direction: ViewerTransactionDirection;
  transactionStatus?: TransactionStatus;
  transactionsDetail?: TransactionDetailDto;
};

export const TransactionDetailActions = ({
  publicID,
  transactionsDetail,
  direction,
  transactionStatus,
  isLoadingStatus,
}: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();
  const isEmployee = useIsCurrentUserEmployee();

  const {
    mutate: cancelTransaction,
    isPending: isCancelTransactionPending,
    isSuccess: isCancelTransactionSuccess,
  } = useCancelTransactionMutation({
    onSuccess: ({ inverseTransactionID }) => {
      if (!inverseTransactionID) {
        return;
      }

      if (isEmployee) {
        return toast.success(
          t(Resources.Transactions.InverseTransactionCreated),
        );
      }

      navigate(`${appUrl("customer-payment-pay")}/${inverseTransactionID}`);
    },
  });

  const shouldShowActionButtons =
    transactionsDetail &&
    direction !== TransactionDirection.Outgoing &&
    transactionStatus !== TransactionStatus.Success &&
    transactionStatus !== TransactionStatus.Cancelled &&
    transactionStatus !== TransactionStatus.Fail;

  // ZS-1676: a Created refund inverse CAN be cancelled (void with unlink — the original becomes
  // refundable again), so refund rows keep the cancel button; an already-refunded original
  // (linked inverse exists) cannot be refunded again — the backend rejects it with a 409.
  const canCancelTransaction =
    !isLoadingStatus &&
    transactionStatus === TransactionStatus.Created &&
    (direction === TransactionDirection.Incoming || isEmployee);

  const canRevertTransaction =
    !isLoadingStatus &&
    transactionStatus === TransactionStatus.Success &&
    !transactionsDetail?.isRefund &&
    !transactionsDetail?.relatedTransactionStatus &&
    (direction === TransactionDirection.Incoming || isEmployee);

  const canPayTransaction =
    transactionsDetail &&
    direction === TransactionDirection.Outgoing &&
    !isEmployee &&
    transactionStatus === TransactionStatus.Created;

  const hasActions =
    shouldShowActionButtons ||
    canPayTransaction ||
    canCancelTransaction ||
    canRevertTransaction;

  if (!hasActions) {
    return null;
  }

  const isCancellingOrCancelled =
    isCancelTransactionPending || isCancelTransactionSuccess;

  return (
    <Stack gap={7}>
      {shouldShowActionButtons && (
        <Box px={[3, 15]} width="100%">
          <TransactionActionButtons
            transactionsDetail={transactionsDetail}
            isInfoHidden
          />
        </Box>
      )}
      {canPayTransaction && <PayTransactionButton publicID={publicID} />}
      {canCancelTransaction && (
        <CancelTransactionButton
          isLoading={isCancellingOrCancelled}
          onClick={() => cancelTransaction(publicID)}
        />
      )}
      {canRevertTransaction && (
        <RevertTransactionButton
          isLoading={isCancellingOrCancelled}
          onClick={() => cancelTransaction(publicID)}
        />
      )}
    </Stack>
  );
};
